import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface ModuleWithProgress {
  id: string
  title: string
  description: string
  category: string
  coins: number
  image_url?: string
  completed: boolean
  order_index: number
  created_at: string
  updated_at: string
}

export const useModulesWithProgress = (category?: string) => {
  const [modules, setModules] = useState<ModuleWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchModulesWithProgress = async () => {
    try {
      setLoading(true)
      
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id

      // Build the query
      let query = supabase
        .from('learn_modules')
        .select('*')
        .order('order_index')

      // Add category filter if specified
      if (category) {
        query = query.eq('category', category)
      }

      const { data: modulesData, error: modulesError } = await query

      if (modulesError) throw modulesError

      // If no user is logged in, return modules without completion status
      if (!userId) {
        const modulesWithoutProgress = (modulesData || []).map(module => ({
          ...module,
          completed: false
        }))
        setModules(modulesWithoutProgress)
        return
      }

      // Get user's completion progress for all modules
      const { data: progressData, error: progressError } = await supabase
        .from('user_content_progress')
        .select(`
          content_id,
          completed,
          learn_content!inner(
            module_id
          )
        `)
        .eq('user_id', userId)
        .eq('completed', true)

      if (progressError) throw progressError

      // Create a map of completed module IDs
      const completedModuleIds = new Set<string>()
      progressData?.forEach(progress => {
        completedModuleIds.add(progress.learn_content.module_id)
      })

      // For each module, check if all its content is completed
      const modulesWithProgress = await Promise.all(
        (modulesData || []).map(async (module) => {
          // Get all content for this module
          const { data: contentData, error: contentError } = await supabase
            .from('learn_content')
            .select('id')
            .eq('module_id', module.id)

          if (contentError) {
            console.error('Error fetching content for module:', module.id, contentError)
            return { ...module, completed: false }
          }

          const totalContent = contentData?.length || 0
          
          // Count completed content for this module
          const completedContent = progressData?.filter(progress => 
            progress.learn_content.module_id === module.id
          ).length || 0

          // Module is completed if all content is completed
          const completed = totalContent > 0 && completedContent === totalContent

          return {
            ...module,
            completed
          }
        })
      )

      setModules(modulesWithProgress)
    } catch (error: any) {
      console.error('Error fetching modules with progress:', error)
      toast({
        title: "Error",
        description: "Failed to fetch modules",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Set up real-time subscription for progress updates
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        fetchModulesWithProgress()
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Set up real-time subscription for content progress changes
  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.user?.id) {
          console.log('No session available for real-time subscription')
          return
        }

        const channel = supabase
          .channel('content-progress-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_content_progress',
              filter: `user_id=eq.${session.user.id}`
            },
            () => {
              console.log('Content progress changed - refreshing modules')
              fetchModulesWithProgress()
            }
          )
          .subscribe()

        return () => {
          supabase.removeChannel(channel)
        }
      } catch (error) {
        console.error('Error setting up real-time subscription:', error)
        return () => {}
      }
    }

    const cleanup = setupRealtimeSubscription()
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.())
    }
  }, [])

  useEffect(() => {
    fetchModulesWithProgress()
  }, [category])

  return {
    modules,
    loading,
    refetch: fetchModulesWithProgress
  }
}
